/**
 * Team Spain v0 — Servicio de Calendario, Concentraciones, Agenda y Sesiones (M2)
 * 
 * Implementa persistencia real en Supabase con RLS, validación de fechas,
 * separación de notas internas de staff y trazabilidad de asistencia.
 */

import { supabase } from '../../lib/supabase'
import { isSupabaseConfigured } from '../../config/env'
import {
  EventItem,
  EventParticipant,
  CampActivity,
  TrainingSession,
  TrainingSessionBlock,
  AttendanceStatus,
  ParticipantRole,
  RosterCandidate,
  ManageRosterResult,
} from '../../types/calendar'

export const EVENT_PARTICIPANTS_SELECT_QUERY = `
  *,
  athletes:athletes!event_participants_athlete_id_fkey (
    id,
    first_name,
    last_name,
    dorsal_number,
    primary_position,
    user_id
  ),
  profiles:profiles!event_participants_user_id_fkey (
    full_name,
    email
  ),
  updated_by_profile:profiles!event_participants_updated_by_fkey (
    full_name
  )
`

export const TRAINING_SESSIONS_SELECT_QUERY = `
  *,
  training_session_blocks:training_session_blocks!training_session_blocks_session_id_fkey (
    *
  )
`

export const calendarService = {
  // ==========================================================================
  // 1. EVENTOS
  // ==========================================================================

  async getEvents(teamId: string): Promise<EventItem[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('team_id', teamId)
      .order('start_time', { ascending: true })

    if (error) {
      throw new Error(`Error al consultar eventos: ${error.message} (${error.code})`)
    }

    return (data || []) as EventItem[]
  },

  async getEventById(eventId: string): Promise<EventItem | null> {
    if (!isSupabaseConfigured() || !supabase) return null

    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .maybeSingle()

    if (error) {
      throw new Error(`Error al consultar evento: ${error.message} (${error.code})`)
    }

    return (data as EventItem) || null
  },

  async getChildEvents(parentEventId: string): Promise<EventItem[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('parent_event_id', parentEventId)
      .order('start_time', { ascending: true })

    if (error) {
      console.error('Error fetching child events:', error)
      return []
    }

    return (data || []) as EventItem[]
  },

  async createEvent(event: {
    organization_id: string
    team_id: string
    season_id: string
    title: string
    event_type: EventItem['event_type']
    status: EventItem['status']
    start_time: string
    end_time: string
    timezone?: string
    location?: string
    description?: string
  }): Promise<EventItem> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    if (new Date(event.end_time) <= new Date(event.start_time)) {
      throw new Error('La fecha y hora de fin debe ser posterior a la de inicio.')
    }

    const { data: userData } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('events')
      .insert({
        organization_id: event.organization_id,
        team_id: event.team_id,
        season_id: event.season_id,
        title: event.title.trim(),
        event_type: event.event_type,
        status: event.status,
        start_time: event.start_time,
        end_time: event.end_time,
        timezone: event.timezone || 'Europe/Madrid',
        location: event.location?.trim() || 'Sede por confirmar',
        description: event.description?.trim() || null,
        created_by: userData.user?.id || null,
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Error al crear evento: ${error.message} (${error.code})`)
    }

    return data as EventItem
  },

  async updateEvent(
    eventId: string,
    updates: Partial<Omit<EventItem, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<EventItem> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    if (updates.start_time || updates.end_time) {
      const currentEvent = await this.getEventById(eventId)
      if (!currentEvent) throw new Error('Evento no encontrado.')

      const newStart = new Date(updates.start_time || currentEvent.start_time)
      const newEnd = new Date(updates.end_time || currentEvent.end_time)

      if (newEnd <= newStart) {
        throw new Error('La hora de fin debe ser posterior a la de inicio.')
      }

      // Comprobar si existen actividades que quedarían fuera del nuevo intervalo
      const { data: acts, error: actErr } = await supabase
        .from('camp_activities')
        .select('id, title, start_time, end_time')
        .eq('event_id', eventId)

      if (!actErr && acts && acts.length > 0) {
        const conflicts = acts.filter((a) => {
          const aStart = new Date(a.start_time)
          const aEnd = new Date(a.end_time)
          return aStart < newStart || aEnd > newEnd
        })

        if (conflicts.length > 0) {
          const tz = currentEvent.timezone || 'Europe/Madrid'
          const conflictDetails = conflicts
            .map(
              (c) =>
                `"${c.title}" (${new Date(c.start_time).toLocaleString('es-ES', { timeZone: tz })} a ${new Date(c.end_time).toLocaleString('es-ES', { timeZone: tz })})`
            )
            .join(', ')
          throw new Error(
            `No se pueden cambiar las fechas del evento porque existen ${conflicts.length} actividad(es) fuera del nuevo intervalo: ${conflictDetails}. Debes resolver o ajustar estas actividades en la Agenda antes de guardar.`
          )
        }
      }
    }

    const { data, error } = await supabase
      .from('events')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', eventId)
      .select()
      .single()

    if (error) {
      throw new Error(`Error al actualizar evento: ${error.message} (${error.code})`)
    }

    return data as EventItem
  },

  async publishEvent(eventId: string): Promise<EventItem> {
    return this.updateEvent(eventId, { status: 'published' })
  },

  async cancelEvent(eventId: string, cancellation_reason: string): Promise<EventItem> {
    if (!cancellation_reason.trim()) {
      throw new Error('Debe proporcionar un motivo para cancelar el evento.')
    }

    return this.updateEvent(eventId, {
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancellation_reason: cancellation_reason.trim(),
    })
  },

  // ==========================================================================
  // 2. CONVOCATORIA Y ASISTENCIA (RSVP)
  // ==========================================================================

  async getEventParticipants(eventId: string): Promise<EventParticipant[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    const { data, error } = await supabase
      .from('event_participants')
      .select(EVENT_PARTICIPANTS_SELECT_QUERY)
      .eq('event_id', eventId)
      .order('role', { ascending: true })

    if (error) {
      throw new Error(`Error al consultar participantes: ${error.message} (${error.code})`)
    }

    // Mapear los datos enriquecidos
    return (data || []).map((item) => {
      const row = item as Record<string, unknown> & {
        id: string
        event_id: string
        team_id: string
        user_id?: string | null
        athlete_id?: string | null
        role: EventParticipant['role']
        attendance_status: EventParticipant['attendance_status']
        comment?: string | null
        responded_at?: string | null
        updated_by?: string | null
        athletes?: { first_name?: string; last_name?: string; dorsal_number?: number; primary_position?: string } | null
        profiles?: { full_name?: string; email?: string } | null
      }
      const athlete = row.athletes
      const directProfile = row.profiles
      const athleteName = athlete ? `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim() : null
      const fullName =
        athleteName ||
        directProfile?.full_name ||
        directProfile?.email ||
        'Participante'

      return {
        id: row.id,
        event_id: row.event_id,
        team_id: row.team_id,
        user_id: row.user_id,
        athlete_id: row.athlete_id,
        role: row.role,
        attendance_status: row.attendance_status,
        comment: row.comment,
        responded_at: row.responded_at,
        updated_by: row.updated_by,
        created_at: row.created_at,
        athlete_name: fullName,
        athlete_jersey: athlete?.dorsal_number,
        athlete_position: athlete?.primary_position,
        user_email: directProfile?.email,
      } as EventParticipant
    })
  },

  async summonTeamRoster(eventId: string, teamId: string): Promise<number> {
    if (!isSupabaseConfigured() || !supabase) return 0

    // 1. Obtener candidatos del equipo
    const candidates = await this.getRosterCandidates(teamId, eventId)
    const athleteIds = candidates.filter((c) => c.type === 'athlete' && c.athlete_id).map((c) => c.athlete_id!)
    const staffUserIds = candidates.filter((c) => c.type === 'staff' && c.user_id).map((c) => c.user_id!)

    const result = await this.manageEventRoster(eventId, athleteIds, staffUserIds)
    return result.total_active
  },

  async getRosterCandidates(teamId: string, eventId: string): Promise<RosterCandidate[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    // 1. Obtener datos del evento
    const event = await this.getEventById(eventId)
    const orgId = event?.organization_id || ''

    // 2. Obtener atletas del equipo con sus datos de roster
    const { data: athletesData, error: athError } = await supabase
      .from('athletes')
      .select(`
        id,
        first_name,
        last_name,
        dorsal_number,
        primary_position,
        user_id,
        roster_entries (
          team_id,
          unit,
          jersey_number
        )
      `)
      .eq('organization_id', orgId)
      .order('dorsal_number', { ascending: true })

    if (athError) {
      throw new Error(`Error al consultar candidatos del roster: ${athError.message}`)
    }

    // 3. Obtener staff del equipo
    const { data: staffMembers, error: staffError } = await supabase
      .from('memberships')
      .select(`
        user_id,
        roles,
        profiles (
          full_name,
          email
        )
      `)
      .or(`team_id.eq.${teamId},team_id.is.null`)
      .eq('is_active', true)

    if (staffError) {
      throw new Error(`Error al consultar staff del equipo: ${staffError.message}`)
    }

    // 4. Obtener participantes actuales del evento
    const { data: participantsData } = await supabase
      .from('event_participants')
      .select('id, athlete_id, user_id, role, attendance_status, comment, withdrawn_at')
      .eq('event_id', eventId)

    const participants = (participantsData || []) as Array<{
      id: string
      athlete_id?: string | null
      user_id?: string | null
      role: ParticipantRole
      attendance_status: AttendanceStatus
      comment?: string | null
      withdrawn_at?: string | null
    }>

    const candidates: RosterCandidate[] = []

    // Mapear Atletas
    for (const rawAth of athletesData || []) {
      const ath = rawAth as Record<string, unknown> & {
        id: string
        first_name?: string
        last_name?: string
        dorsal_number?: number
        primary_position?: string
        user_id?: string | null
        roster_entries?: Array<{ team_id: string; unit?: string; jersey_number?: number }>
      }
      const teamRoster = Array.isArray(ath.roster_entries)
        ? ath.roster_entries.find((r) => r.team_id === teamId)
        : null

      const currentPart = participants.find((p) => p.athlete_id === ath.id)
      const fullName = `${ath.first_name || ''} ${ath.last_name || ''}`.trim() || 'Atleta'

      candidates.push({
        id: `athlete_${ath.id}`,
        type: 'athlete',
        athlete_id: ath.id,
        user_id: ath.user_id || undefined,
        name: fullName,
        dorsal_number: teamRoster?.jersey_number ?? ath.dorsal_number ?? undefined,
        position: ath.primary_position || undefined,
        unit: (teamRoster?.unit as 'offense' | 'defense' | 'staff' | 'all') || 'all',
        role: 'athlete',
        has_account: Boolean(ath.user_id),
        current_status: currentPart?.attendance_status,
        withdrawn_at: currentPart?.withdrawn_at || undefined,
        comment: currentPart?.comment || undefined,
      })
    }

    // Mapear Staff
    const staffUserMap = new Set<string>()
    for (const rawSt of staffMembers || []) {
      const st = rawSt as Record<string, unknown> & {
        user_id: string
        roles: string[]
        profiles?: { full_name?: string; email?: string } | null
      }
      if (!st.user_id || staffUserMap.has(st.user_id)) continue
      staffUserMap.add(st.user_id)

      // Evitar duplicar staff si ya es un atleta del roster con ese user_id
      const isAthleteUser = candidates.some((c) => c.user_id === st.user_id)
      if (isAthleteUser) continue

      const currentPart = participants.find((p) => p.user_id === st.user_id && !p.athlete_id)
      const prof = st.profiles
      const staffName = prof?.full_name || prof?.email || 'Miembro de Staff'

      const role: ParticipantRole = st.roles.includes('coach')
        ? 'coach'
        : st.roles.includes('medic')
          ? 'medic'
          : st.roles.includes('admin')
            ? 'admin'
            : 'staff'

      candidates.push({
        id: `staff_${st.user_id}`,
        type: 'staff',
        user_id: st.user_id,
        name: staffName,
        role,
        unit: 'staff',
        has_account: true,
        user_email: prof?.email,
        current_status: currentPart?.attendance_status,
        withdrawn_at: currentPart?.withdrawn_at || undefined,
        comment: currentPart?.comment || undefined,
      })
    }

    return candidates
  },

  async manageEventRoster(
    eventId: string,
    athleteIds: string[],
    staffUserIds: string[]
  ): Promise<ManageRosterResult> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    const { data, error } = await supabase.rpc('manage_event_roster', {
      p_event_id: eventId,
      p_athlete_ids: athleteIds,
      p_staff_user_ids: staffUserIds,
    })

    if (error) {
      throw new Error(`Error al gestionar convocatoria: ${error.message} (${error.code})`)
    }

    return data as ManageRosterResult
  },

  async updateMyAttendance(
    eventId: string,
    status: AttendanceStatus,
    comment?: string
  ): Promise<EventParticipant> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuario no autenticado.')

    // Buscar la fila del participante correspondiente al usuario o a su atleta vinculado
    const { data: participantRow, error: findError } = await supabase
      .from('event_participants')
      .select('id')
      .eq('event_id', eventId)
      .or(`user_id.eq.${user.id},athlete_id.in.(select id from athletes where user_id='${user.id}')`)
      .maybeSingle()

    if (findError || !participantRow) {
      throw new Error('No se encontró una convocatoria activa para tu cuenta en este evento.')
    }

    const { data, error } = await supabase
      .from('event_participants')
      .update({
        attendance_status: status,
        comment: comment?.trim() || null,
        responded_at: new Date().toISOString(),
        updated_by: user.id,
      })
      .eq('id', participantRow.id)
      .select()
      .single()

    if (error) {
      throw new Error(`Error al registrar tu asistencia: ${error.message} (${error.code})`)
    }

    return data as EventParticipant
  },

  async updateParticipantAttendanceByStaff(
    participantId: string,
    status: AttendanceStatus,
    comment?: string
  ): Promise<EventParticipant> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuario no autenticado.')

    const { data, error } = await supabase
      .from('event_participants')
      .update({
        attendance_status: status,
        comment: comment !== undefined ? comment.trim() || null : undefined,
        updated_by: user.id,
        responded_at: new Date().toISOString(),
        withdrawn_at: status === 'withdrawn' ? new Date().toISOString() : null,
      })
      .eq('id', participantId)
      .select()
      .single()

    if (error) {
      throw new Error(`Error al actualizar asistencia del participante: ${error.message}`)
    }

    return data as EventParticipant
  },

  // ==========================================================================
  // 3. CONCENTRACIONES Y AGENDA (ACTIVIDADES Y NOTAS INTERNAS)
  // ==========================================================================

  async getCampActivities(eventId: string): Promise<CampActivity[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    // 1. Obtener actividades públicas/compartidas
    const { data: activities, error } = await supabase
      .from('camp_activities')
      .select('*')
      .eq('event_id', eventId)
      .order('start_time', { ascending: true })
      .order('order_index', { ascending: true })

    if (error) {
      throw new Error(`Error al consultar agenda: ${error.message} (${error.code})`)
    }

    // 2. Intentar obtener notas internas (RLS devolverá filas solo si es staff)
    const activityIds = (activities || []).map((a) => a.id)
    const internalNotesMap = new Map<string, string>()

    if (activityIds.length > 0) {
      const { data: notesData } = await supabase
        .from('camp_activity_internal_notes')
        .select('activity_id, internal_notes')
        .in('activity_id', activityIds)

      if (notesData) {
        for (const n of notesData) {
          internalNotesMap.set(n.activity_id, n.internal_notes)
        }
      }
    }

    return (activities || []).map((act) => ({
      ...act,
      internal_notes: internalNotesMap.get(act.id),
    })) as CampActivity[]
  },

  async getAllCampActivities(teamId: string): Promise<CampActivity[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    const { data: activities, error } = await supabase
      .from('camp_activities')
      .select('*')
      .eq('team_id', teamId)
      .order('start_time', { ascending: true })
      .order('order_index', { ascending: true })

    if (error) {
      throw new Error(`Error al consultar agenda del equipo: ${error.message} (${error.code})`)
    }

    const activityIds = (activities || []).map((a) => a.id)
    const internalNotesMap = new Map<string, string>()

    if (activityIds.length > 0) {
      const { data: notesData } = await supabase
        .from('camp_activity_internal_notes')
        .select('activity_id, internal_notes')
        .in('activity_id', activityIds)

      if (notesData) {
        for (const n of notesData) {
          internalNotesMap.set(n.activity_id, n.internal_notes)
        }
      }
    }

    return (activities || []).map((act) => ({
      ...act,
      internal_notes: internalNotesMap.get(act.id),
    })) as CampActivity[]
  },

  async createCampActivity(
    activity: {
      event_id: string
      team_id: string
      title: string
      activity_type: CampActivity['activity_type']
      start_time: string
      end_time: string
      location?: string
      responsibles?: string
      target_unit?: CampActivity['target_unit']
      order_index?: number
      shared_notes?: string
      session_id?: string
    },
    internalNotes?: string
  ): Promise<CampActivity> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    if (new Date(activity.end_time) <= new Date(activity.start_time)) {
      throw new Error('La hora de fin de la actividad debe ser posterior a la de inicio.')
    }

    // Validar intervalo contra el evento padre
    const parentEvent = await this.getEventById(activity.event_id)
    if (!parentEvent) {
      throw new Error('Evento no encontrado para la actividad.')
    }

    const actStart = new Date(activity.start_time)
    const actEnd = new Date(activity.end_time)
    const evStart = new Date(parentEvent.start_time)
    const evEnd = new Date(parentEvent.end_time)

    if (actStart < evStart || actEnd > evEnd) {
      const tz = parentEvent.timezone || 'Europe/Madrid'
      const evStartFmt = evStart.toLocaleString('es-ES', { timeZone: tz })
      const evEndFmt = evEnd.toLocaleString('es-ES', { timeZone: tz })
      const actStartFmt = actStart.toLocaleString('es-ES', { timeZone: tz })
      const actEndFmt = actEnd.toLocaleString('es-ES', { timeZone: tz })
      throw new Error(
        `La actividad "${activity.title}" (${actStartFmt} a ${actEndFmt}) debe estar dentro del intervalo de la concentración (${evStartFmt} a ${evEndFmt}).`
      )
    }

    const { data, error } = await supabase
      .from('camp_activities')
      .insert({
        event_id: activity.event_id,
        team_id: activity.team_id,
        title: activity.title.trim(),
        activity_type: activity.activity_type,
        start_time: activity.start_time,
        end_time: activity.end_time,
        location: activity.location?.trim() || null,
        responsibles: activity.responsibles?.trim() || null,
        target_unit: activity.target_unit || 'all',
        order_index: activity.order_index ?? 0,
        shared_notes: activity.shared_notes?.trim() || null,
        session_id: activity.session_id || null,
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Error al crear actividad: ${error.message} (${error.code})`)
    }

    // Si se especificaron notas internas de staff, guardarlas en la tabla física protegida
    if (internalNotes?.trim()) {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('camp_activity_internal_notes').insert({
        activity_id: data.id,
        team_id: activity.team_id,
        internal_notes: internalNotes.trim(),
        updated_by: user?.id || null,
      })
    }

    return { ...data, internal_notes: internalNotes?.trim() } as CampActivity
  },

  async updateCampActivity(
    activityId: string,
    updates: Partial<Omit<CampActivity, 'id' | 'created_at' | 'updated_at'>>,
    internalNotes?: string
  ): Promise<CampActivity> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend de Supabase no configurado.')
    }

    if (updates.start_time && updates.end_time) {
      if (new Date(updates.end_time) <= new Date(updates.start_time)) {
        throw new Error('La hora de fin debe ser posterior a la de inicio.')
      }
    }

    // Si se modifican fechas, validar contra el evento
    if (updates.start_time || updates.end_time) {
      const { data: currentAct } = await supabase
        .from('camp_activities')
        .select('event_id, start_time, end_time, title')
        .eq('id', activityId)
        .single()

      if (currentAct) {
        const parentEvent = await this.getEventById(currentAct.event_id)
        if (parentEvent) {
          const actStart = new Date(updates.start_time || currentAct.start_time)
          const actEnd = new Date(updates.end_time || currentAct.end_time)
          const evStart = new Date(parentEvent.start_time)
          const evEnd = new Date(parentEvent.end_time)

          if (actStart < evStart || actEnd > evEnd) {
            const tz = parentEvent.timezone || 'Europe/Madrid'
            const evStartFmt = evStart.toLocaleString('es-ES', { timeZone: tz })
            const evEndFmt = evEnd.toLocaleString('es-ES', { timeZone: tz })
            const actStartFmt = actStart.toLocaleString('es-ES', { timeZone: tz })
            const actEndFmt = actEnd.toLocaleString('es-ES', { timeZone: tz })
            throw new Error(
              `La actividad "${currentAct.title}" (${actStartFmt} a ${actEndFmt}) debe estar dentro del intervalo de la concentración (${evStartFmt} a ${evEndFmt}).`
            )
          }
        }
      }
    }

    const { data, error } = await supabase
      .from('camp_activities')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', activityId)
      .select()
      .single()

    if (error) {
      throw new Error(`Error al actualizar actividad: ${error.message} (${error.code})`)
    }

    if (internalNotes !== undefined) {
      const { data: { user } } = await supabase.auth.getUser()
      if (internalNotes.trim()) {
        await supabase.from('camp_activity_internal_notes').upsert({
          activity_id: activityId,
          team_id: data.team_id,
          internal_notes: internalNotes.trim(),
          updated_by: user?.id || null,
          updated_at: new Date().toISOString(),
        })
      } else {
        await supabase
          .from('camp_activity_internal_notes')
          .delete()
          .eq('activity_id', activityId)
      }
    }

    return { ...data, internal_notes: internalNotes?.trim() } as CampActivity
  },

  async deleteCampActivity(activityId: string): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) return

    const { error } = await supabase
      .from('camp_activities')
      .delete()
      .eq('id', activityId)

    if (error) {
      throw new Error(`Error al eliminar actividad: ${error.message}`)
    }
  },

  // ==========================================================================
  // 4. SESIONES DE ENTRENAMIENTO Y BLOQUES
  // ==========================================================================

  async getTrainingSessions(teamId: string, eventId?: string): Promise<TrainingSession[]> {
    if (!isSupabaseConfigured() || !supabase) return []

    let query = supabase
      .from('training_sessions')
      .select(TRAINING_SESSIONS_SELECT_QUERY)
      .eq('team_id', teamId)
      .order('session_date', { ascending: true })
      .order('start_time', { ascending: true })

    if (eventId) {
      query = query.eq('event_id', eventId)
    }

    const { data, error } = await query

    if (error) {
      throw new Error(`Error al consultar sesiones de entrenamiento: ${error.message}`)
    }

    return (data || []).map((item) => {
      const row = item as Record<string, unknown> & {
        training_session_blocks?: TrainingSessionBlock[]
      }
      return {
        ...row,
        blocks: (row.training_session_blocks || []).sort(
          (a: TrainingSessionBlock, b: TrainingSessionBlock) => a.order_index - b.order_index
        ),
      }
    }) as TrainingSession[]
  },

  async createTrainingSession(
    session: {
      team_id: string
      event_id?: string
      title: string
      session_date: string
      start_time: string
      end_time: string
      objectives?: string
      responsibles?: string
      target_unit?: TrainingSession['target_unit']
    },
    blocks?: Array<Omit<TrainingSessionBlock, 'id' | 'session_id' | 'team_id' | 'created_at' | 'updated_at'>>
  ): Promise<TrainingSession> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend no configurado.')
    }

    const { data: newSession, error: sessError } = await supabase
      .from('training_sessions')
      .insert({
        team_id: session.team_id,
        event_id: session.event_id || null,
        title: session.title.trim(),
        session_date: session.session_date,
        start_time: session.start_time,
        end_time: session.end_time,
        objectives: session.objectives?.trim() || null,
        responsibles: session.responsibles?.trim() || 'Staff Técnico',
        target_unit: session.target_unit || 'all',
      })
      .select()
      .single()

    if (sessError) {
      throw new Error(`Error al crear sesión: ${sessError.message}`)
    }

    let insertedBlocks: TrainingSessionBlock[] = []
    if (blocks && blocks.length > 0) {
      const blocksPayload = blocks.map((b, idx) => ({
        session_id: newSession.id,
        team_id: session.team_id,
        order_index: b.order_index ?? idx,
        block_type: b.block_type,
        title: b.title.trim(),
        start_time: b.start_time,
        duration_minutes: b.duration_minutes,
        target_unit: b.target_unit || 'all',
        notes: b.notes?.trim() || null,
      }))

      const { data: blkData, error: blkError } = await supabase
        .from('training_session_blocks')
        .insert(blocksPayload)
        .select()

      if (blkError) {
        throw new Error(`Error al insertar bloques de sesión: ${blkError.message}`)
      }
      insertedBlocks = (blkData || []) as TrainingSessionBlock[]
    }

    return { ...newSession, blocks: insertedBlocks } as TrainingSession
  },

  async duplicateTrainingSession(
    originalSessionId: string,
    newDate: string,
    newTitle?: string
  ): Promise<TrainingSession> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Backend no configurado.')
    }

    // 1. Obtener sesión original con sus bloques
    const { data: original, error: origError } = await supabase
      .from('training_sessions')
      .select('*, training_session_blocks:training_session_blocks!training_session_blocks_session_id_fkey(*)')
      .eq('id', originalSessionId)
      .single()

    if (origError || !original) {
      throw new Error('No se encontró la sesión a duplicar.')
    }

    // 2. Crear copia de la sesión (sin asistencia ni registros históricos)
    const title = newTitle || `${original.title} (Copia)`
    const newSession = await this.createTrainingSession(
      {
        team_id: original.team_id,
        event_id: original.event_id,
        title,
        session_date: newDate,
        start_time: original.start_time,
        end_time: original.end_time,
        objectives: original.objectives,
        responsibles: original.responsibles,
        target_unit: original.target_unit,
      },
      (original.training_session_blocks || []).map((b: TrainingSessionBlock) => ({
        order_index: b.order_index,
        block_type: b.block_type,
        title: b.title,
        start_time: b.start_time,
        duration_minutes: b.duration_minutes,
        target_unit: b.target_unit,
        notes: b.notes,
      }))
    )

    return newSession
  },
}
